import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner, SectionLabel, Pill, Button } from "../components/Primitives";

export default function Kingdoms() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/kingdoms").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const generate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    setGenerating(true);
    try {
      const res = await api.post("/kingdoms/generate", { topic: topic.trim() });
      toast.success(`Generated profile: ${res.data.name}`);
      setTopic("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate that profile. Try another topic.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="kingdoms-page">
      <div className="border-b border-edge bg-ash">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <SectionLabel>Civilizations</SectionLabel>
          <h1 className="font-serif text-5xl font-semibold text-bone lg:text-6xl">Kingdoms & Empires</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted">
            Wealthy, organized, and globally connected states that rivaled their contemporaries across the world.
          </p>

          <form onSubmit={generate} className="mt-10 flex max-w-xl flex-col gap-3 sm:flex-row" data-testid="generate-form">
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Generate a profile — e.g. 'Kingdom of Dahomey'"
              data-testid="generate-input"
              className="flex-1 border border-edge bg-ink px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
            />
            <Button type="submit" variant="gold" disabled={generating} data-testid="generate-btn">
              <Sparkles className="h-4 w-4" />
              {generating ? "Researching…" : "Generate"}
            </Button>
          </form>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted">
            AI-generated on demand · powered by Claude
          </p>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-12">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((k, i) => (
              <motion.div
                key={k.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: (i % 6) * 0.06 }}
              >
                <Link
                  to={`/kingdoms/${k.slug}`}
                  data-testid={`kingdom-card-${k.slug}`}
                  className="group block h-full overflow-hidden border border-edge bg-ash transition-all duration-300 hover:-translate-y-1 hover:border-gold/50"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={k.image_url}
                      alt={k.name}
                      className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ash via-ash/20 to-transparent" />
                    <div className="absolute left-4 top-4 flex gap-2">
                      <Pill tone="gold">{k.type}</Pill>
                      {k.ai_generated && <Pill tone="blood">AI</Pill>}
                    </div>
                  </div>
                  <div className="p-6">
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-gold">{k.era}</span>
                    <h3 className="mt-2 font-serif text-2xl text-bone group-hover:text-gold">{k.name}</h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted">{k.summary}</p>
                    <div className="mt-4 flex items-center gap-2 text-sm text-gold opacity-0 transition-opacity group-hover:opacity-100">
                      Read more <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
